import mixpanel, { Config, Dict } from "mixpanel-browser"

import { SORT_KEY_VALUE_MAPPING } from "constant"
import { getGAId } from "utils/local-storage"
import { isDev } from "utils/ssr"

import { EventENUMV2, UserProfilePropertiesV2 } from "./mixpanel/types"

const CONFIG: Partial<Config> = {
  api_host: "https://wet.whimstay.com",
  cdn: `https://wet.whimstay.com/cdn`,
  ignore_dnt: true,
  autocapture: true,
  record_sessions_percent: 5,
  record_mask_text_selector: "",
  record_block_selector: "",
}

const mixpanelV2 = mixpanel.init(
  process.env.MIXPANEL_TOKEN_V2 ||
    process.env.NEXT_PUBLIC_MIXPANEL_TOKEN_V2 ||
    "",
  CONFIG,
  "versionV2",
)

const envCheck = !isDev()

class MixpanelV2 {
  public gaId: string | null = null

  private identify(id: string | number) {
    if (envCheck) mixpanelV2.identify(`${id}`)
  }

  private getCjeCookie() {
    if (envCheck) {
      try {
        if (typeof document === "undefined") return null
        const m = document.cookie.match(/(?:^|; )cje=([^;]*)/)
        return m ? decodeURIComponent(m[1]) : null
      } catch (error) {
        console.log({ error })
        return null
      }
    }
  }

  private track(name: string, props: Dict) {
    if (!this.gaId) {
      const gaId = getGAId() // include ga_id for all mixpanelv2 events
      this.gaId = gaId || null
    }

    const cje = this.getCjeCookie()

    if (cje) {
      // eslint-disable-next-line no-param-reassign
      props.cje = cje
    } else {
      try {
        mixpanelV2.unregister?.("cje")
      } catch (error) {
        console.log({ error })
      }
      // eslint-disable-next-line no-param-reassign
      if ("cje" in props) delete props.cje
    }

    if (envCheck) {
      // eslint-disable-next-line no-param-reassign
      props.ga_id = this.gaId

      mixpanelV2.track(name, props)
    }
  }

  private people = {
    set: (props: Dict) => {
      if (envCheck) mixpanelV2.people.set(props)
    },
    increment: (props: Dict | string) => {
      if (envCheck) mixpanelV2.people.increment(props)
    },
  }

  private reset() {
    if (envCheck) mixpanelV2.reset()
  }

  // when user comes to vacation-rentals page or searches/apply filters.
  VIEW_SEARCH_RESULTS_PAGE(data: {
    terms: string
    city: string
    dates: string
    filters: string
    locationName: string
    maxPrice: string | number
    methodOfSearch: string
    minPrice: string | number
    noOfGuest: string | number
    numberOfBathroom: string | number
    numberOfBedroom: string | number
    numberOfBeds: string | number
    numberOfDaysBeforeCheckin?: number
    shouldDisplayTotalPrice: boolean
    state: string
    country?: string
    totalNights: string | number
    totalProperty: string | number
    sortBy: string
    pageOrigin: string
    lat?: number
    lng?: number
    bounds: null | string
    isPolygonUsed?: boolean
    multiUnitPropertyCount: number | string
    expandedPropertiesReturned?: number | null
    expandedRadiusMiles?: number | null
    pviVersion: string
    theme?: string
    multiUnitGridABTestVariant?: string
    preferredHostProperties?: number
  }) {
    // DONE
    this.track(EventENUMV2.VIEW_SEARCH_RESULTS_PAGE, {
      "mp schema version": "1.0",
      term: data?.terms,
      "city searched": data?.city,
      "state searched": data?.state,
      "country searched": data?.country,
      "dates selected": data?.dates,
      "num of nights": data?.totalNights,
      "num of guests": data?.noOfGuest,
      "num of properties returned": data?.totalProperty,
      "num of bedrooms selected": data?.numberOfBedroom,
      "num of beds selected": data?.numberOfBeds,
      "num of bathrooms selected": data?.numberOfBathroom,
      "min price selected": data?.minPrice,
      "max price selected": data?.maxPrice,
      "filters selected": data?.filters,
      "method of search": data?.methodOfSearch,
      "num of days until check in": data?.numberOfDaysBeforeCheckin || 0,
      "price display setting": data.shouldDisplayTotalPrice
        ? SORT_KEY_VALUE_MAPPING.totalTripCost
        : SORT_KEY_VALUE_MAPPING.nightlyPrice,
      "sort results by": data.sortBy,
      "page origin": data?.pageOrigin || "NA",
      latitude: data?.lat || "NA",
      longitude: data?.lng || "NA",
      bounds: data?.bounds || "NA",
      "is polygon used": data?.isPolygonUsed,
      "num of multi-unit properties returned": data?.multiUnitPropertyCount,
      "expanded properties returned": data?.expandedPropertiesReturned,
      "expanded radius miles": data?.expandedRadiusMiles,
      theme: data.theme,
      "pvi algorithm": data?.pviVersion || "NA",
      "multi unit grid variant": data?.multiUnitGridABTestVariant,
      "preferred host properties": data?.preferredHostProperties,
    })
    this.INCREMENT_TOTAL_NUMBER_OF_SEARCH_RESULTS_PAGE_VIEWS()
    this.SET_DATE_OF_LAST_SEARCH()
    if (data.locationName) {
      this.SET_LOCATION_OF_LAST_SEARCH(data.locationName)
    }
  }

  // when user clicks on a property on search results page.
  CLICK_PROPERTY_ON_SEARCH_RESULT_PAGE(data: {
    ranking: number
    propertyId: string
    savedPrice: string | number
    actualPrice: string | number
    discountPrice: string | number
    city: string
    dates: string
    filters: string
    maxPrice: string | number
    methodOfSearch: "map area" | "search result page"
    minPrice: string | number
    noOfGuest: string | number
    numberOfBathroom: string | number
    numberOfBedroom: string | number
    numberOfBeds: string | number
    numberOfDaysBeforeCheckin?: number
    shouldDisplayTotalPrice: boolean
    state: string
    country?: string
    totalNights: string | number
    sortBy: string
    propertyRating?: string | number | null
  }) {
    // DONE
    this.track(EventENUMV2.CLICK_PROPERTY_ON_SEARCH_RESULT_PAGE, {
      "mp schema version": "1.0",
      "property id": data?.propertyId,
      ranking: data?.ranking,
      "saved price": data?.savedPrice,
      "actual price": data?.actualPrice,
      "discount price": data?.discountPrice,
      "city searched": data?.city,
      "state searched": data?.state,
      "country searched": data?.country,
      "dates selected": data?.dates,
      "num of nights": data?.totalNights,
      "num of guests": data?.noOfGuest,
      "num of bedrooms selected": data?.numberOfBedroom,
      "num of beds selected": data?.numberOfBeds,
      "num of bathrooms selected": data?.numberOfBathroom,
      "min price selected": data?.minPrice,
      "max price selected": data?.maxPrice,
      "filters selected": data?.filters,
      "method of search": data?.methodOfSearch,
      "num of days until check in": data?.numberOfDaysBeforeCheckin || 0,
      "price display setting": data.shouldDisplayTotalPrice
        ? SORT_KEY_VALUE_MAPPING.totalTripCost
        : SORT_KEY_VALUE_MAPPING.nightlyPrice,
      "sort results by": data.sortBy,
      "property rating": data?.propertyRating,
    })
  }

  // when user comes to property detail page or select/changes dates on the same page.
  VIEW_PROPERTY_DETAILS_PAGE(data: {
    city: string
    dates: string
    hostFee: string | number
    numOfDaysUntilCheckIn: string | number
    numberOfBedroom: string | number
    pageOrigin: string
    propertyId: string
    propertyRatingStarSum?: number | null
    propertyRatingUserCount?: number | null
    propertyTypeDesc: string
    state: string
    totalAmount: string | number
    totalNights: string | number
    totalSaving: string | number
    totalServiceFee?: number | string | null
    totalDisplayPriceExcludingTaxes?: number | string | null
    description_variant: string
    actual_price: string
    saved_price: string
    strike_price: string
    featured: boolean
    aiSummaryShown: boolean
    savingsAmountBasedOnPrecisionPricing?: string
    isPPShown?: boolean
    priceDifference?: number | null
    totalDisplayPriceWithTaxes?: number | string | null
    availabilityError?: boolean
    highlightsShown?: boolean
    airbnbAmenitiesEnhanced?: boolean
    selfCheckInAvailable?: "Yes" | "No"
    airbnbSavingsPercent?: number
    vrboSavingsPercent?: number
    isMultiUnit?: boolean
    muPDPFlow?: "direct_to_pdp" | "via_mu_detail"
    crossSellVersion?: "New" | "Old"
    preferredHost?: boolean
  }) {
    // DONE
    this.track(EventENUMV2.VIEW_PROPERTY_DETAILS_PAGE, {
      "mp schema version": "1.0",
      "property id": data?.propertyId || "NA",
      "city of property": data?.city,
      "state of property": data?.state,
      "dates selected": data?.dates,
      "num of nights": data?.totalNights,
      "num of days until check in": data?.numOfDaysUntilCheckIn,
      "total booking cost excluding taxes": data?.totalAmount,
      "total host fee": data?.hostFee,
      "total savings": data?.totalSaving,
      "type of property": data?.propertyTypeDesc,
      "num of bedrooms": data?.numberOfBedroom,
      "page origin": data?.pageOrigin || "NA",
      "property rating": data.propertyRatingStarSum || 0,
      "num of reviews": data.propertyRatingUserCount || 0,
      "total service fee": data.totalServiceFee || 0,
      "total display price excluding taxes":
        data?.totalDisplayPriceExcludingTaxes || "NA",
      description_variant: data?.description_variant,
      "search page price": data?.actual_price,
      "search page save amount": data?.saved_price,
      "search page strike out price": data?.strike_price,
      "is featured": data?.featured,
      "ai summary shown": data?.aiSummaryShown,
      "pdp savings": data?.savingsAmountBasedOnPrecisionPricing || "NA",
      "precision price shown": data?.isPPShown || false,
      "price difference": data?.priceDifference || "NA",
      "total display price with taxes":
        data?.totalDisplayPriceWithTaxes || "NA",
      "availability error": data?.availabilityError || false,
      "highlights shown": data?.highlightsShown || false,
      "airbnb amenities enhanced": data?.airbnbAmenitiesEnhanced || false,
      "self check-in available": data?.selfCheckInAvailable || "No",
      "airbnb savings pct": data?.airbnbSavingsPercent || 0,
      "vrbo savings pct": data?.vrboSavingsPercent || 0,
      "is multi unit": data?.isMultiUnit || false,
      "mu pdp flow": data?.muPDPFlow || "NA",
      "cross-sell version": data?.crossSellVersion || "NA",
      "preferred host": data?.preferredHost || false,
    })
    this.INCREMENT_NUMBER_OF_DETAIL_PAGE_VIEWS()
    this.SET_ID_OF_LAST_PROPERTY_VIEWED(data.propertyId)
  }

  // when user click on remove map boundary button on search page.
  CLICK_REMOVE_MAP_BOUNDARY(data: {
    city: string
    country: string
    state: string
  }) {
    this.track(EventENUMV2.CLICK_REMOVE_MAP_BOUNDARY, {
      "city searched": data.city,
      "state searched": data.state,
      "country searched": data.country,
    })
  }

  // when user click on price pin button on search page.
  CLICK_PRICE(data: {
    city: string
    country: string
    state: string
    propertyId: string
  }) {
    this.track(EventENUMV2.CLICK_PRICE, {
      "city searched": data.city,
      "state searched": data.state,
      "country searched": data.country,
      "property id": data.propertyId,
    })
  }

  // when user click on dot button on search page.
  CLICK_DOT(data: {
    city: string
    country: string
    state: string
    propertyId: string
  }) {
    this.track(EventENUMV2.CLICK_DOT, {
      "city searched": data.city,
      "state searched": data.state,
      "country searched": data.country,
      "property id": data.propertyId,
    })
  }

  // when user comes to property detail page and clicks on the nearby destination links.
  CLICK_NEARBY_DESTINATION(data: { destination: string; propertyId: string }) {
    // DONE
    this.track(EventENUMV2.CLICK_NEARBY_DESTINATION, {
      destination: data.destination,
      "property id": data.propertyId,
    })
  }

  // Click on nearby property from pdp page
  CLICK_SIMILAR_PROPERTY() {
    this.track(EventENUMV2.CLICK_SIMILAR_PROPERTY, {})
  }

  CLICK_IMAGES(data: { propertyId: string }) {
    // DONE
    this.track(EventENUMV2.CLICK_IMAGES, {
      "property id": data.propertyId,
    })
  }

  // when user comes traveler detail page in payment flow.
  VIEW_CHECKOUT_DETAILS_PAGE(data: {
    city: string
    hostFee: string | number
    numOfDaysUntilCheckIn: string | number
    propertyId: string
    propertyRatingStarSum?: number | null
    propertyRatingUserCount?: number | null
    state: string
    totalAmount: string | null
    totalNights: string | number
    totalSaving: string | number
    totalServiceFee?: string | number | null
    totalTax: string | number
  }) {
    //  DONE
    this.track(EventENUMV2.VIEW_CHECKOUT_DETAILS_PAGE, {
      "mp schema version": "1.0",
      "property id": data?.propertyId || "NA",
      "city of property": data?.city,
      "state of property": data?.state,
      "num of nights": data?.totalNights,
      "num of days until check in": data?.numOfDaysUntilCheckIn,
      "total booking cost": data?.totalAmount,
      "total host fee": data?.hostFee,
      "total savings": data?.totalSaving,
      "total tax": data?.totalTax,
      "property rating": data.propertyRatingStarSum || 0,
      "num of reviews": data.propertyRatingUserCount || 0,
      "total service fee": data.totalServiceFee || 0,
    })
  }

  // when user comes to card details page.
  VIEW_CHECKOUT_PAYMENT_PAGE(data: {
    city: string
    hostFee: number
    numOfDaysUntilCheckIn: string | number
    propertyId: string
    propertyRatingStarSum?: number | null
    propertyRatingUserCount?: number | null
    state: string
    totalAmount: string | number
    totalNights: string | number
    totalSaving: string | number
    totalServiceFee?: string | number
    totalTax: string | number
    isMultiUnit?: boolean // to check user comes from multi units detail page or not
    guestCount?: number
    paymentOption: string
    optIn?: string
    theme?: string
    isLoggedIn?: boolean
    version?: string
  }) {
    // DONE
    this.track(EventENUMV2.VIEW_CHECKOUT_PAYMENT_PAGE, {
      "mp schema version": "1.0",
      "property id": data?.propertyId || "NA",
      "city of property": data?.city,
      "state of property": data?.state,
      "num of nights": data?.totalNights,
      "num of days until check in": data?.numOfDaysUntilCheckIn,
      "total booking cost": data?.totalAmount,
      "total host fee": data?.hostFee,
      "total savings": data?.totalSaving,
      "total tax": data?.totalTax,
      "property rating": data.propertyRatingStarSum || 0,
      "num of reviews": data.propertyRatingUserCount || 0,
      "total service fee": data.totalServiceFee || 0,
      "is multi unit": data.isMultiUnit,
      ...(data.guestCount && { "num of guests": data?.guestCount }),
      "payment option": data.paymentOption,
      "opt in": data.optIn,
      "is logged in": data.isLoggedIn,
      theme: data.theme,
      version: data.version,
    })
  }

  CLICK_CHANGE_DATES(data: {
    city: string
    hostFee: number
    numOfDaysUntilCheckIn: string | number
    propertyId: string
    propertyRatingStarSum?: number | null
    propertyRatingUserCount?: number | null
    state: string
    totalAmount: string | number
    totalNights: string | number
    totalSaving: string | number
    totalServiceFee?: string | number
    totalTax: string | number
    isMultiUnit?: boolean // to check user comes from multi units detail page or not
    guestCount?: number
    cancellation_policy?: string
  }) {
    // DONE
    this.track(EventENUMV2.CLICK_CHANGE_DATES, {
      "mp schema version": "1.0",
      "property id": data?.propertyId || "NA",
      "city of property": data?.city,
      "state of property": data?.state,
      "num of nights": data?.totalNights,
      "num of days until check in": data?.numOfDaysUntilCheckIn,
      "total booking cost": data?.totalAmount,
      "total host fee": data?.hostFee,
      "total savings": data?.totalSaving,
      "total tax": data?.totalTax,
      "property rating": data.propertyRatingStarSum || 0,
      "num of reviews": data.propertyRatingUserCount || 0,
      "total service fee": data.totalServiceFee || 0,
      "is multi unit": data.isMultiUnit,
      "cancellation policy": data.cancellation_policy,
      ...(data.guestCount && { "num of guests": data?.guestCount }),
    })
  }

  CLICK_PAYMENT_TYPE(data: {
    propertyId: string
    paymentMethod: string
    paymentMethodSaving: number
    totalAmount: number
    totalHostFee: number
    totalSaving: number
    totalServiceFee: number
    totalTax: number
  }) {
    this.track(EventENUMV2.CLICK_PAYMENT_TYPE, {
      "mp schema version": "1.0",
      "property id": data.propertyId || "NA",
      "payment method": data.paymentMethod || "NA",
      "payment method saving": data.paymentMethodSaving || 0,
      "total booking cost": data.totalAmount || 0,
      "total host fee": data.totalHostFee || 0,
      "total paid amount": (data.totalAmount || 0) - (data.totalSaving || 0),
      "total savings": data.totalSaving || 0,
      "total service fee": data.totalServiceFee || 0,
      "total tax": data.totalTax || 0,
    })
  }

  // when user comes to thank you page (after booking completion).
  // this event should be triggered only once it should be not triggered again on refresh.
  VIEW_CHECKOUT_CONFIRMATION_PAGE(data: {
    bookingNumber: string
    city: string
    hostFee: string | number
    isAffirmPayment: boolean | string
    isSplitPaymentInvitee: boolean
    isTravelProtected: boolean
    numOfDaysUntilCheckIn: string | number
    numberOfBedroom: string | number
    promocode?: string
    promocodeAmount?: string
    promocodeApplied?: boolean
    propertyId: string
    propertyRatingStarSum?: number | null
    propertyRatingUserCount?: number | null
    propertyTypeDesc: string
    state: string
    totalAmount: string | number
    totalNights: string | number
    totalSaving: string | number
    totalServiceFees: string | number
    totalTax: string | number
    isDifferentAddress: boolean
    paymentMode: string
    travelerInsurance: string
    paymentType?: string
    totalPaidAmount?: number
    pageOrigin?: string
  }) {
    // DONE
    this.track(EventENUMV2.VIEW_CHECKOUT_CONFIRMATION_PAGE, {
      "mp schema version": "1.0",
      "property id": data?.propertyId || "NA",
      "city of property": data?.city,
      "state of property": data?.state,
      "num of nights": data?.totalNights,
      "num of days until check in": data?.numOfDaysUntilCheckIn,
      "total booking cost": data?.totalAmount,
      "total host fee": data?.hostFee,
      "total savings": data?.totalSaving,
      "total service fee": data?.totalServiceFees,
      "total tax": data?.totalTax,
      "booking number": data?.bookingNumber,
      "type of property": data?.propertyTypeDesc,
      "num of bedrooms": data?.numberOfBedroom,
      "reserved for another guest": data?.isDifferentAddress,
      "payment method": data?.paymentMode,
      "travel insurance": data?.travelerInsurance,
      // "is affirm being used": data?.isAffirmPayment,
      // "is travel insurance purchased": data?.isTravelProtected,
      "property rating": data.propertyRatingStarSum || 0,
      "num of reviews": data.propertyRatingUserCount || 0,
      ...(data.promocodeApplied
        ? {
            promocode: data.promocode,
            "promocode applied": true,
            "promocode amount": data.promocodeAmount,
          }
        : {
            "promocode applied": false,
          }),
      "is split payment": data.isSplitPaymentInvitee,
      "payment type": data?.paymentType || "NA",
      "total paid amount": data?.totalPaidAmount || 0,
      "page origin": data?.pageOrigin || "NA",
    })
  }

  // when user comes to do staged payment in manage payment page (after booking completion).
  // this event should be triggered only once it should be not triggered again on refresh.
  VIEW_STAGED_PAYMENT_CONFIRMATION_PAGE(data: {
    bookingNumber: string
    city: string
    hostFee: string | number
    isAffirmPayment: boolean | string
    isSplitPaymentInvitee: boolean
    isTravelProtected: boolean
    numOfDaysUntilCheckIn: string | number
    numberOfBedroom: string | number
    promocode?: string
    promocodeAmount?: string
    promocodeApplied?: boolean
    propertyId: string
    propertyRatingStarSum?: number | null
    propertyRatingUserCount?: number | null
    propertyTypeDesc: string
    state: string
    totalAmount: string | number
    totalNights: string | number
    totalSaving: string | number
    totalServiceFees: string | number
    totalTax: string | number
    isDifferentAddress: boolean
    paymentMode: string
    travelerInsurance: string
    paymentType?: string
    totalPaidAmount?: number
    pageOrigin?: string
  }) {
    // DONE
    this.track(EventENUMV2.VIEW_STAGED_PAYMENT_CONFIRMATION_PAGE, {
      "mp schema version": "1.0",
      "property id": data?.propertyId || "NA",
      "city of property": data?.city,
      "state of property": data?.state,
      "num of nights": data?.totalNights,
      "num of days until check in": data?.numOfDaysUntilCheckIn,
      "total booking cost": data?.totalAmount,
      "total host fee": data?.hostFee,
      "total savings": data?.totalSaving,
      "total service fee": data?.totalServiceFees,
      "total tax": data?.totalTax,
      "booking number": data?.bookingNumber,
      "type of property": data?.propertyTypeDesc,
      "num of bedrooms": data?.numberOfBedroom,
      "reserved for another guest": data?.isDifferentAddress,
      "payment method": data?.paymentMode,
      "travel insurance": data?.travelerInsurance,
      // "is affirm being used": data?.isAffirmPayment,
      // "is travel insurance purchased": data?.isTravelProtected,
      "property rating": data.propertyRatingStarSum || 0,
      "num of reviews": data.propertyRatingUserCount || 0,
      ...(data.promocodeApplied
        ? {
            promocode: data.promocode,
            "promocode applied": true,
            "promocode amount": data.promocodeAmount,
          }
        : {
            "promocode applied": false,
          }),
      "is split payment": data.isSplitPaymentInvitee,
      "payment type": data?.paymentType || "NA",
      "total paid amount": data?.totalPaidAmount || 0,
      "page origin": data?.pageOrigin || "NA",
    })
  }

  // on the thank you page after booking completion when user clicks on the submit button
  // on the booking experience rating modal.
  CLICK_SUBMIT_NPS() {
    this.track(EventENUMV2.CLICK_SUBMIT_NPS, {})
  }

  // on the booking experience feedback modal.
  CLICK_SUBMIT_NPS_FEEDBACK() {
    this.track(EventENUMV2.CLICK_SUBMIT_NPS_FEEDBACK, {})
  }

  // on booking details page.
  VIEW_BOOKING_DETAILS_PAGE() {
    // DONE
    this.track(EventENUMV2.VIEW_BOOKING_DETAILS_PAGE, {
      "mp schema version": "1.0",
    })
  }

  // when user click "Discover Adventures" button on thank you page
  CLICK_VIATOR(data: {
    bookingNumber: string
    propertyId: string
    cityOfProperty: string
    stateOfProperty: string
  }) {
    this.track(EventENUMV2.CLICK_VIATOR, {
      "booking number": data.bookingNumber,
      "property id": data.propertyId,
      "city of property": data.cityOfProperty,
      "state of property": data.stateOfProperty,
      "page origin": "checkout confirmation page",
    })
  }

  VIEW_SPRING_BREAK_DEALS_PAGE(data: { pageVariant: string }) {
    this.track(EventENUMV2.VIEW_SPRING_BREAK_DEALS_PAGE, {
      "mp schema version": "1.0",
      "page variant": data?.pageVariant?.toLowerCase() || "base-master",
    })
  }

  // on home page.
  VIEW_HOME_PAGE(data: { theme: string }) {
    // DONE
    this.track(EventENUMV2.VIEW_HOME_PAGE, {
      "mp schema version": "1.0",
      theme: data.theme,
    })
  }

  // every time login modal opens.
  VIEW_LOGIN_PROMPT(data: { pageOrigin: string }) {
    // DONE
    this.track(EventENUMV2.VIEW_LOGIN_PROMPT, {
      "mp schema version": "1.0",
      "page origin": data?.pageOrigin?.toLowerCase() || "NA",
    })
  }

  // every time registration modal opens.
  VIEW_REGISTRATION_PROMO() {
    // DONE
    this.track(EventENUMV2.VIEW_REGISTRATION_PROMO, {})
  }

  // when sign-up is completed(isSuccess: true) or fails(isSuccess: false).
  COMPLETE_REGISTRATION(data: {
    isSuccess: boolean
    method: string
    pageOrigin: string
    firstName?: string
    lastName?: string
    emailId?: string
    country?: string
    isGoogle?: boolean
    isApple?: boolean
  }) {
    // DONE
    this.track(EventENUMV2.COMPLETE_REGISTRATION, {
      "mp schema version": "1.0",
      "page origin": data?.pageOrigin?.toLowerCase(),
      "method of registration": data?.method,
      "is successful": data?.isSuccess,
      "first name": data?.firstName || "NA",
      "last name": data?.lastName || "NA",
      "email id": data?.emailId || "NA",
      "country searched": data?.country,
      "is google": data?.isGoogle || false,
      "is apple": data?.isApple || false,
    })
  }

  // when sign-in/logIn is completed(isSuccess: true) or fails(isSuccess: false).
  COMPLETE_SIGN_IN(data: {
    isSuccess: boolean
    method: string
    pageOrigin: string
    firstName?: string
    lastName?: string
    emailId?: string
    isGoogle?: boolean
    isApple?: boolean
  }) {
    // DONE
    this.track(EventENUMV2.COMPLETE_SIGN_IN, {
      "mp schema version": "1.0",
      "page origin": data?.pageOrigin?.toLowerCase(),
      "method of sign in": data?.method,
      "is successful": data?.isSuccess,
      "first name": data?.firstName || "NA",
      "last name": data?.lastName || "NA",
      "email id": data?.emailId || "NA",
      "is google": data?.isGoogle || false,
      "is apple": data?.isApple || false,
    })
  }

  // when user clicks on book now button.
  CLICKED_BOOK_NOW(data: {
    city: string
    hostFee: number | string
    numOfDaysUntilCheckIn: number | string
    numberOfBedroom: number | string
    propertyId: string
    propertyRatingStarSum?: number | null
    propertyRatingUserCount?: number | null
    propertyTypeDesc: string
    state: string
    totalAmount: number | string
    totalNights: number | string
    totalSaving: number | string
    totalServiceFee?: number | string | null
    isMultiUnit?: boolean // to check user comes from multi units detail page or not
    guestCount?: number
  }) {
    // DONE
    this.track(EventENUMV2.CLICKED_BOOK_NOW, {
      "mp schema version": "1.0",
      "property id": data?.propertyId || "NA",
      "city of property": data?.city,
      "state of property": data?.state,
      "num of nights": data?.totalNights,
      "num of days until check in": data?.numOfDaysUntilCheckIn,
      "total booking cost": data?.totalAmount,
      "total host fee": data?.hostFee,
      "total savings": data?.totalSaving,
      "type of property": data?.propertyTypeDesc,
      "num of bedrooms": data?.numberOfBedroom,
      "property rating": data.propertyRatingStarSum || 0,
      "num of reviews": data.propertyRatingUserCount || 0,
      "total service fee": data.totalServiceFee || 0,
      "is multi unit": data?.isMultiUnit || false,
      ...(data.guestCount && { "num of guests": data?.guestCount }),
    })
    this.INCREMENT_NUMBER_OF_BOOK_BUTTON_CLICKS()
  }

  // when user clicks on recommended property.
  CLICKED_FEATURED_PROPERTY(data: {
    city: string
    numberOfBedroom: number | string
    propertyId: string
    propertyTypeDesc: string
    state: string
  }) {
    // DONE
    this.track(EventENUMV2.CLICKED_FEATURED_PROPERTY, {
      "mp schema version": "1.0",
      "property id": data?.propertyId || "NA",
      "city of property": data?.city,
      "state of property": data?.state,
      "type of property": data?.propertyTypeDesc,
      "num of bedrooms": data?.numberOfBedroom,
    })
  }

  // when user comes to about us page.
  VIEW_ABOUT_US_PAGE(data?: { pageOrigin: string }) {
    this.track(EventENUMV2.VIEW_ABOUT_US_PAGE, {
      "mp schema version": "1.0",
      "page origin": data?.pageOrigin?.toLowerCase() || "NA",
    })
  }

  WEBSITE_ERROR(data: {
    pageType: "404" | "500"
    path?: string
    errorDetail?: string
    sentry_error_id?: string
  }) {
    this.track(EventENUMV2.WEBSITE_ERROR, {
      "mp schema version": "1.0",
      "error page type": data.pageType,
      url: data.path || "NA",
      error_detail: data.errorDetail || "NA",
      sentry_error_id: data.sentry_error_id || "NA",
    })
  }

  // when user clicks on popular destination.
  CLICKED_DESTINATION_PROPERTY(data: { destination: string }) {
    // DONE
    this.track(EventENUMV2.CLICKED_DESTINATION_PROPERTY, {
      "mp schema version": "1.0",
      destination: data?.destination,
    })
  }

  // when user click on faq question in regional landing page or about us page.
  FAQ_VIEWED(data: { faq_category: string; faq_title: string }) {
    // DONE
    this.track(EventENUMV2.FAQ_VIEWED, {
      "mp schema version": "1.0",
      "faq category": data?.faq_category,
      "faq title": data?.faq_title,
    })
  }

  // when user click on travel inspiration section in regional landing page.
  BLOG_VIEWED(data: { blog_category: string; blog_title: string }) {
    // DONE
    this.track(EventENUMV2.BLOG_VIEWED, {
      "mp schema version": "1.0",
      "blog title": data?.blog_title,
      "blog category": data?.blog_category,
    })
  }

  // when user clicks on heart icon.
  CLICKED_SAVE_PROPERTY(data: {
    city: string
    numberOfBedroom: string | number
    propertyId: string
    propertyTypeDesc: string
    state: string
  }) {
    // DONE
    this.track(EventENUMV2.CLICKED_SAVE_PROPERTY, {
      "mp schema version": "1.0",
      "property id": data?.propertyId || "NA",
      "city of property": data?.city,
      "state of property": data?.state,
      "type of property": data?.propertyTypeDesc,
      "num of bedrooms": data?.numberOfBedroom,
    })
  }

  INSURANCE_OPTION_SELECTED(data: { insurance_option: string }) {
    // DONE
    this.track(EventENUMV2.INSURANCE_OPTION_SELECTED, {
      "mp schema version": "1.0",
      "insurance option": data?.insurance_option,
    })
  }

  PAYMENT_TIMING_CHOSEN(data: { payment_timing: string }) {
    // DONE
    this.track(EventENUMV2.PAYMENT_TIMING_CHOSEN, {
      "mp schema version": "1.0",
      "payment timing": data?.payment_timing,
    })
  }

  STAGED_PAYMENT_CHOICE(data: {
    staged_payment_option?: "pay now" | "split payment"
  }) {
    this.track(EventENUMV2.STAGED_PAYMENT_CHOICE, {
      "mp schema version": "1.0",
      "staged payment option": data.staged_payment_option,
    })
  }

  TRAVELLER_INFO_COMPLETE() {
    // DONE
    this.track(EventENUMV2.TRAVELLER_INFO_COMPLETE, {
      "mp schema version": "1.0",
    })
  }

  GUEST_CHECKOUT_STARTED() {
    // DONE
    this.track(EventENUMV2.GUEST_CHECKOUT_STARTED, {
      "mp schema version": "1.0",
    })
  }

  // on share button click from property detail page.
  CLICKED_SHARE_PROPERTY() {
    // DONE
    this.track(EventENUMV2.CLICKED_SHARE_PROPERTY, {
      "mp schema version": "1.0",
    })
  }

  // on region request button click only if logged in.
  REQUEST_REGION(data: { region: string }) {
    // DONE
    this.track(EventENUMV2.REQUEST_REGION, {
      "mp schema version": "1.0",
      region: data?.region,
    })
  }

  // when user comes to property-managers page.
  VIEW_HOST_LANDING_PAGE(data?: { pageOrigin: string }) {
    // DONE
    this.track(EventENUMV2.VIEW_HOST_LANDING_PAGE, {
      "mp schema version": "1.0",
      "page origin": data?.pageOrigin?.toLowerCase() || "NA",
    })
  }

  // when user clicks on playstore or applestore icons
  CLICK_DOWNLOAD(data: { type: string }) {
    this.track(EventENUMV2.CLICK_DOWNLOAD, {
      "mp schema version": "1.0",
      type: data?.type?.toLowerCase() || "NA",
    })
  }

  // when user comes to blog details page.
  VIEW_BLOG(data: { blog_title: string }) {
    this.track(EventENUMV2.VIEW_BLOG, {
      "mp schema version": "1.0",
      title: data?.blog_title || "NA",
    })
  }

  // on pay now button click.
  CLICK_SUBMIT_PAYMENT(data: {
    propertyRatingStarSum?: number | null
    propertyRatingUserCount?: number | null
  }) {
    // DONE
    this.track(EventENUMV2.CLICK_SUBMIT_PAYMENT, {
      "mp schema version": "1.0",
      "property rating": data.propertyRatingStarSum || 0,
      "num of reviews": data.propertyRatingUserCount || 0,
      "is split payment": false,
    })
  }

  // To track promocode promocode usage for successful payments
  ENTER_PROMOCODE(data: {
    promocode: string
    promocodeAmount: number
    promocodeApplied: "true" | "false"
  }) {
    // DONE
    this.track(EventENUMV2.ENTER_PROMOCODE, {
      promocode: data.promocode,
      "promocode applied": data.promocodeApplied,
      "promocode amount": data.promocodeAmount.toFixed(2),
    })
  }

  // when user opens total price breakdown popup
  VIEW_PRICE_BREAKDOWN_POPOVER() {
    this.track(EventENUMV2.VIEW_PRICE_BREAKDOWN_POPOVER, {})
  }

  // when email is verifed through email verification link.
  EMAIL_VERIFICATION(data: {
    email: string
    userId: number
    emailVerified: boolean
  }) {
    this.track(EventENUMV2.EMAIL_VERIFICATION, {
      email: data.email,
      "user id": data.userId,
      "verified email": data.emailVerified,
    })
  }

  IDENTIFY(id: string | number) {
    this.identify(id)
  }

  // to reset current mixpanel session.
  RESET_USER() {
    this.reset()
  }

  // to set users data in mixpanel
  SET_PEOPLE(data: Dict) {
    this.people.set(data)
  }

  GET_DISTINCT_ID() {
    return mixpanelV2.get_distinct_id() as string
  }

  INCREMENT(data: string | Dict) {
    const distinctId = mixpanelV2.get_distinct_id() as string
    if (distinctId) mixpanelV2.identify(distinctId)
    this.people.increment(data)
  }

  // Profile Properties
  INCREMENT_NUMBER_OF_BOOK_BUTTON_CLICKS() {
    this.INCREMENT(UserProfilePropertiesV2.NUMBER_OF_BOOK_BUTTON_CLICKS)
  }

  INCREMENT_NUMBER_OF_DETAIL_PAGE_VIEWS() {
    this.INCREMENT(UserProfilePropertiesV2.NUMBER_OF_DETAIL_PAGE_VIEWS)
  }

  INCREMENT_TOTAL_NUMBER_OF_SEARCH_RESULTS_PAGE_VIEWS() {
    this.INCREMENT(
      UserProfilePropertiesV2.TOTAL_NUMBER_OF_SEARCH_RESULTS_PAGE_VIEWS,
    )
  }

  SET_ID_OF_LAST_PROPERTY_VIEWED(propertyId: string) {
    this.people.set({
      [UserProfilePropertiesV2.ID_OF_LAST_PROPERTY_VIEWED]: propertyId,
    })
  }

  SET_IS_GVR_USER() {
    mixpanelV2.people.set_once({
      [UserProfilePropertiesV2.GVR_USER]: true,
      [UserProfilePropertiesV2.GVR_USER_FIRST_VISITED_DATE]: new Date(),
    })
  }

  SET_DATE_OF_LAST_SEARCH() {
    this.people.set({
      [UserProfilePropertiesV2.DATE_OF_LAST_SEARCH]: new Date(),
    })
  }

  SET_LOCATION_OF_LAST_SEARCH(location: string) {
    this.people.set({
      [UserProfilePropertiesV2.LOCATION_OF_LAST_SEARCH]: location,
    })
  }

  SET_NUMBER_OF_PROPERTIES_FAVORITED(count: number) {
    this.people.set({
      [UserProfilePropertiesV2.NUMBER_OF_PROPERTIES_FAVORITED]: count,
    })
  }

  SET_VERIFIED_EMAIL(isVerified: boolean) {
    this.people.set({
      [UserProfilePropertiesV2.VERIFIED_EMAIL]: isVerified,
    })
  }

  CLICK_TOTAL_TRIP_SAVING(savingType: string) {
    this.track(EventENUMV2.CLICK_TOTAL_TRIP_SAVING, {
      "savings display settings": savingType,
    })
  }

  CLICK_PRICE_RANGE(data: {
    priceDisplay: string
    minPrice: string
    maxPrice: string
    sortBy: string
  }) {
    this.track(EventENUMV2.CLICK_PRICE_RANGE, {
      "price display setting": data.priceDisplay,
      "min price selected": data.minPrice,
      "max price selected": data.maxPrice,
      "sort results by": data.sortBy,
    })
  }

  // when click on popular choices in vacation-rentals page
  CLICK_POPULAR_CHOICES() {
    this.track(EventENUMV2.CLICK_POPULAR_CHOICES, {})
  }

  // Booking flow error
  BOOKING_ERROR(data: {
    errorDisplay: string
    propertyId: string
    typeOfError: string
    errorResponse: string
    bookingNo: string
    errorSource: "API Failure" | "Stripe Failure"
    prequoteResponseId?: string
  }) {
    this.track(EventENUMV2.BOOKING_ERROR, {
      "type of error": data.typeOfError || "",
      "error response": data.errorResponse || "NA",
      "error display": data.errorDisplay || "",
      "property id": data.propertyId,
      "booking number": data.bookingNo,
      "error source": data.errorSource,
      "prequote response id": data?.prequoteResponseId || "NA",
    })
  }

  CLICK_REFERRAL(pageOrigin: string) {
    this.track(EventENUMV2.CLICK_REFERRAL, {
      "page origin": pageOrigin, // home page/property detail page
    })
  }

  // on reviews page
  VIEW_REVIEWS_PAGE(pageOrigin?: string) {
    this.track(EventENUMV2.VIEW_REVIEWS_PAGE, {
      ...(pageOrigin && { "page origin": pageOrigin }),
    })
  }

  // click on leave a review - trust pilot link
  CLICK_LEAVE_REVIEW() {
    this.track(EventENUMV2.CLICK_LEAVE_REVIEW, {})
  }

  // id verification click events
  CLICK_ID_VERIFICATION() {
    this.track(EventENUMV2.CLICK_ID_VERIFICATION, {})
  }

  VIEW_REFERRALS_PAGE() {
    this.track(EventENUMV2.VIEW_REFERRALS_PAGE, {})
  }

  VIEW_PENDING_ID_VERIFICATION_PAGE() {
    this.track(EventENUMV2.VIEW_PENDING_ID_VERIFICATION_PAGE, {})
  }

  // ==================Multi Units event================
  CLICK_MAP_CARD(data: {
    propertyTitle: string
    location: string
    noOfUnits: number
    displayedPrice: string
  }) {
    this.track(EventENUMV2.CLICK_MAP_CARD, {
      "property title": data.propertyTitle,
      location: data.location,
      "number of units": data.noOfUnits,
      "displayed price": data.displayedPrice,
    })
  }

  CLICK_VIEW_CARD_LIST(data: {
    propertyTitle: string
    location: string
    noOfUnits: number
    displayedPrice: string
  }) {
    this.track(EventENUMV2.CLICK_VIEW_CARD_LIST, {
      "property title": data.propertyTitle,
      location: data.location,
      "number of units": data.noOfUnits,
      "displayed price": data.displayedPrice,
    })
  }

  DATE_SELECTION(data: { datesSelected: string; noOfNights: number }) {
    this.track(EventENUMV2.DATE_SELECTION, {
      "dates selected": data.datesSelected,
      "num of nights": data.noOfNights,
    })
  }

  GUEST_SELECTION(data: {
    adultCount: number
    childCount: number
    petAllowed: boolean
    pageOrigin?: string
  }) {
    this.track(EventENUMV2.GUEST_SELECTION, {
      "num of adult": data.adultCount,
      "num of child": data.childCount,
      "pets allowed": data.petAllowed,
      "page origin": data.pageOrigin,
    })
  }

  UNIT_FILTER_APPLIED(data: { filterType: string; filterValue: number }) {
    this.track(EventENUMV2.UNIT_FILTER_APPLIED, {
      "filter type": data.filterType,
      "filter value": data.filterValue,
    })
  }

  // RBO properties events
  RBO_BOOKING_RECEIVED(data: { bookingId: string }) {
    this.track(EventENUMV2.RBO_BOOKING_RECEIVED, {
      "booking id": data.bookingId,
    })
  }

  RBO_BOOKING_CANCELED(data: { bookingId: string }) {
    this.track(EventENUMV2.RBO_BOOKING_CANCELED, {
      "booking id": data.bookingId,
    })
  }

  // --- booking page events starts ---
  CLICK_START_EXPLORING_BUTTON() {
    this.track(EventENUMV2.CLICK_START_EXPLORING_BUTTON, {})
  }

  CLICK_SET_UP_ACCOUNT() {
    this.track(EventENUMV2.CLICK_SET_UP_ACCOUNT, {})
  }

  CLICK_PENDING_BOOKINGS(data: { total: number }) {
    this.track(EventENUMV2.CLICK_PENDING_BOOKINGS, {
      "total bookings": data.total,
    })
  }

  CLICK_UPCOMING_BOOKINGS(data: { total: number }) {
    this.track(EventENUMV2.CLICK_UPCOMING_BOOKINGS, {
      "total bookings": data.total,
    })
  }

  CLICK_PAST_BOOKINGS(data: { total: number }) {
    this.track(EventENUMV2.CLICK_PAST_BOOKINGS, {
      "total bookings": data.total,
    })
  }

  CLICK_CANCELLED_BOOKINGS(data: { total: number }) {
    this.track(EventENUMV2.CLICK_CANCELLED_BOOKINGS, {
      "total bookings": data.total,
    })
  }

  CLICK_BOOKING_CARD_DETAILS(data: { bookingNumber: string }) {
    this.track(EventENUMV2.CLICK_BOOKING_CARD_DETAILS, {
      "booking number": data.bookingNumber,
    })
  }

  CLICK_WRITE_A_REVIEW(data: { bookingNumber: string; propertyId: string }) {
    this.track(EventENUMV2.CLICK_WRITE_A_REVIEW, {
      "booking number": data.bookingNumber,
      "property id": data?.propertyId,
    })
  }

  CLICK_TOTAL_COST_PANEL(data: { bookingNumber: string; propertyId: string }) {
    this.track(EventENUMV2.CLICK_TOTAL_COST_PANEL, {
      "booking number": data.bookingNumber,
      "property id": data?.propertyId,
    })
  }

  CLICK_REBOOK(data: { bookingNumber: string; propertyId: string }) {
    this.track(EventENUMV2.CLICK_REBOOK, {
      "booking number": data.bookingNumber,
      "property id": data?.propertyId,
    })
  }

  CLICK_ADDRESS(data: { bookingNumber: string; propertyId: string }) {
    this.track(EventENUMV2.CLICK_ADDRESS, {
      "booking number": data.bookingNumber,
      "property id": data?.propertyId,
    })
  }

  CLICK_CALL(data: { bookingNumber: string; propertyId: string }) {
    this.track(EventENUMV2.CLICK_CALL, {
      "booking number": data.bookingNumber,
      "property id": data?.propertyId,
    })
  }

  CLICK_EMAIL(data: { bookingNumber: string; propertyId: string }) {
    this.track(EventENUMV2.CLICK_EMAIL, {
      "booking number": data.bookingNumber,
      "property id": data?.propertyId,
    })
  }

  CLICK_ADD_INSURANCE(data: { bookingNumber: string; propertyId: string }) {
    this.track(EventENUMV2.CLICK_ADD_INSURANCE, {
      "booking number": data.bookingNumber,
      "property id": data?.propertyId,
    })
  }
  // --- booking page events ends ---

  // cancellation booking flow events
  CLICK_CHANGE_BOOKING(data: { bookingNo: string }) {
    this.track(EventENUMV2.CLICK_CHANGE_BOOKING, {
      "booking number": data.bookingNo,
    })
  }

  CLICK_MODIFY_BOOKING(data: { bookingNo: string }) {
    this.track(EventENUMV2.CLICK_MODIFY_BOOKING, {
      "booking number": data.bookingNo,
    })
  }

  CLICK_CANCEL_BOOKING(data: { bookingNo: string; reason: string }) {
    this.track(EventENUMV2.CLICK_CANCEL_BOOKING, {
      "booking number": data.bookingNo,
      "selected reason": data.reason,
    })
  }

  CLICK_CHAT_WITH_HOST(data: { bookingNo: string; initiatedFrom: string }) {
    this.track(EventENUMV2.CLICK_CHAT_WITH_HOST, {
      "booking number": data.bookingNo,
      "initiated from": data.initiatedFrom,
    })
  }

  BLOG_CLICKED_DESKTOP(data: { source: "Header" | "Footer" }) {
    this.track(EventENUMV2.BLOG_CLICKED_DESKTOP, {
      Source: data.source,
      deviceType: "Desktop",
    })
  }

  BLOG_CLICKED_MOBILE(data: { source: "Header" | "Footer" }) {
    this.track(EventENUMV2.BLOG_CLICKED_MOBILE, {
      Source: data.source,
      deviceType: "Mobile",
    })
  }

  // precision pricing events
  SHOW_PRECISION_PRICING(data: {
    propertyId: string
    apiStatus: string
    airbnbUrl: string
    airbnbTotal: number | string
    airbnbError: string
    vrboUrl: string
    vrboTotal: number | string
    vrboError: string
    errorMessage?: string
    whimstayTotal?: number | string
    datesSelected?: string
    precisionPriceShown?: boolean
    whimstayExclusive?: boolean
    airbnbDisplayType?: "live" | "estimated"
    vrboDisplayType?: "live" | "estimated"
    airbnbSavingsPercent?: number
    vrboSavingsPercent?: number
    airbnbUserViewPriceSource?: string
    vrboUserViewPriceSource?: string
    airbnbTotalEstimated: number | string
    vrboTotalEstimated: number | string
    airbnbExternalLinkShown: boolean
    vrboExternalLinkShown: boolean
    competitorCountShown: number
    vrboDataType: string
    airbnbDataType: string
  }) {
    this.track(EventENUMV2.SHOW_PRECISION_PRICING, {
      "property id": data.propertyId,
      "api status": data.apiStatus,
      "airbnb url": data.airbnbUrl,
      "airbnb total": data.airbnbTotal,
      "airbnb error": data.airbnbError,
      "vrbo url": data.vrboUrl,
      "vrbo total": data.vrboTotal,
      "vrbo error": data.vrboError,
      "whimstay total": data.whimstayTotal || "",
      "dates selected": data.datesSelected || "",
      "error message": data.errorMessage || "",
      "precision price shown": data.precisionPriceShown || false,
      "whimstay exclusive": data.whimstayExclusive || false,
      "airbnb display type": data.airbnbDisplayType || "",
      "vrbo display type": data.vrboDisplayType || "",
      "airbnb savings pct": data?.airbnbSavingsPercent || 0,
      "vrbo savings pct": data?.vrboSavingsPercent || 0,
      "airbnb user view price source": data?.airbnbUserViewPriceSource || "NA",
      "vrbo user view price source": data?.vrboUserViewPriceSource || "NA",
      "airbnb total estimated": data?.airbnbTotalEstimated || "NA",
      "vrbo total estimated": data?.vrboTotalEstimated || "NA",
      "airbnb external link shown": data?.airbnbExternalLinkShown || false,
      "vrbo external link shown": data?.vrboExternalLinkShown || false,
      "competitor count shown": data?.competitorCountShown || "NA",
      "airbnb data type": data?.airbnbDataType || "NA",
      "vrbo data type": data?.vrboDataType || "NA",
    })
  }

  CLICK_AIRBNB_LINK(data: {
    propertyId: string
    apiStatus: string
    airbnbUrl: string
    airbnbTotal: number | string
    airbnbError: string
    airbnbAvailable: boolean | null
    errorMessage?: string
    whimstayTotal?: number | string
    datesSelected?: string
  }) {
    this.track(EventENUMV2.CLICK_AIRBNB_LINK, {
      "property id": data.propertyId,
      "api status": data.apiStatus,
      "airbnb url": data.airbnbUrl,
      "airbnb total": data.airbnbTotal,
      "airbnb error": data.airbnbError,
      "airbnb available": data.airbnbAvailable,
      "whimstay total": data.whimstayTotal || "",
      "dates selected": data.datesSelected || "",
      "error message": data.errorMessage || "",
    })
  }

  CLICK_VRBO_LINK(data: {
    propertyId: string
    apiStatus: string
    vrboUrl: string
    vrboTotal: number | string
    vrboError: string
    vrboAvailable: boolean | null
    whimstayTotal?: number | string
    datesSelected?: string
    errorMessage?: string
  }) {
    this.track(EventENUMV2.CLICK_VRBO_LINK, {
      "property id": data.propertyId,
      "api status": data.apiStatus,
      "vrbo url": data.vrboUrl,
      "vrbo total": data.vrboTotal,
      "vrbo error": data.vrboError,
      "vrbo available": data.vrboAvailable,
      "whimstay total": data.whimstayTotal || "",
      "dates selected": data.datesSelected || "",
      "error message": data.errorMessage || "",
    })
  }
  //precision pricing events end

  // pdp events start
  PDP_DESCRIPTION_CLICK(data: { propertyId: string }) {
    this.track(EventENUMV2.PDP_DESCRIPTION_CLICK, {
      "property id": data.propertyId,
    })
  }

  PDP_THINGS_TO_KNOW_CLICK(data: { propertyId: string }) {
    this.track(EventENUMV2.PDP_THINGS_TO_KNOW_CLICK, {
      "property id": data.propertyId,
    })
  }

  PDP_CANCELLATION_POLICY_CLICK(data: { propertyId: string }) {
    this.track(EventENUMV2.PDP_CANCELLATION_POLICY_CLICK, {
      "property id": data.propertyId,
    })
  }

  PDP_AMENITIES_CLICK(data: { propertyId: string }) {
    this.track(EventENUMV2.PDP_AMENITIES_CLICK, {
      "property id": data.propertyId,
    })
  }

  PDP_ABOUT_HOST_CLICK(data: { propertyId: string; placement: string }) {
    this.track(EventENUMV2.PDP_ABOUT_HOST_CLICK, {
      "property id": data.propertyId,
      placement: data.placement,
    })
  }

  PDP_GUEST_REVIEWS_CLICK(data: { propertyId: string }) {
    this.track(EventENUMV2.PDP_GUEST_REVIEWS_CLICK, {
      "property id": data.propertyId,
    })
  }

  VIEW_REVIEWS(data: {
    propertyId: string
    reviewCount: number
    rating: number
  }) {
    this.track(EventENUMV2.VIEW_REVIEWS, {
      "property id": data.propertyId,
      "review count": data.reviewCount,
      rating: data.rating,
    })
  }

  CLICK_REVIEW_SHOW_MORE(data: { propertyId: string; rating: number }) {
    this.track(EventENUMV2.CLICK_REVIEW_SHOW_MORE, {
      "property id": data.propertyId,
      rating: data.rating,
    })
  }
  // pdp events end

  // Thankyou/confirmation page events START
  NOTIFY_GUESTS(data: {
    guestEmails: string
    propertyId: string
    bookingDates: string
    bookingNumber: string
    pageOrigin: string
  }) {
    this.track(EventENUMV2.NOTIFY_GUESTS, {
      "other guest emails": data.guestEmails,
      "property id": data.propertyId,
      "booking dates": data.bookingDates,
      "booking number": data.bookingNumber,
      "page origin": data?.pageOrigin || "",
    })
  }

  SAVE_TO_CALENDAR(data: {
    bookingNo: string
    propertyId: string
    bookingDates: string
    pageOrigin: string
  }) {
    this.track(EventENUMV2.SAVE_TO_CALENDAR, {
      "booking number": data.bookingNo,
      "property id": data.propertyId,
      "booking dates": data.bookingDates,
      "page origin": data?.pageOrigin,
    })
  }

  DOWNLOAD_PRINT(data: {
    propertyId: string
    bookingDates: string
    pageOrigin: string
  }) {
    this.track(EventENUMV2.DOWNLOAD_PRINT, {
      "property id": data.propertyId,
      "booking dates": data.bookingDates,
      "page origin": data?.pageOrigin,
    })
  }

  GET_THE_APP(data: { propertyId: string; bookingDates: string }) {
    this.track(EventENUMV2.GET_THE_APP, {
      "property id": data.propertyId,
      "booking dates": data.bookingDates,
    })
  }

  CONTACT_US(data: {
    propertyId: string
    bookingDates: string
    pageOrigin: string
  }) {
    this.track(EventENUMV2.CONTACT_US, {
      "property id": data.propertyId,
      "booking dates": data.bookingDates,
      "page source": data?.pageOrigin || "",
    })
  }

  VERIFY_NOW(data: {
    bookingNo: string
    propertyId: string
    pageOrigin: string
  }) {
    this.track(EventENUMV2.VERIFY_NOW, {
      "booking number": data.bookingNo,
      "property id": data.propertyId,
      "page origin": data.pageOrigin,
    })
  }
  // Thankyou/confirmation page events END

  // On getting Prequote response
  CLICK_AIRBNB_PREQUOTE(data: {
    selectedDates?: string
    propertyId?: string
    cleaningFees?: number
    totalRent?: number
    cleaningFeeSource?: string
    origin?: string
  }) {
    this.track(EventENUMV2.CLICK_AIRBNB_PREQUOTE, {
      "dates selected": data?.selectedDates,
      "property id": data?.propertyId || "NA",
      "total cleaning fees": data?.cleaningFees,
      "total rent": data?.totalRent,
      "cleaning fees source": data?.cleaningFeeSource,
      "page origin": data?.origin || "NA",
    })
  }

  // Event to trigger – fired when AI summary block enters in viewport
  VIEW_AI_SUMMARY(data: { propertyId: string }) {
    this.track(EventENUMV2.VIEW_AI_SUMMARY, {
      "property id": data.propertyId,
    })
  }

  // price drop events
  CLICK_PRICE_DROP_WATCH(data: {
    type: "Watch Property" | "Watch Trip"
    propertyId?: string
    term?: string | null
    datesSelected: string
    numOfGuests: number
    pageOrigin: "search results page" | "property detail page"
    totalDisplayPriceExcludingTaxes: number
  }) {
    this.track(EventENUMV2.CLICK_PRICE_DROP_WATCH, {
      type: data.type,
      "property id": data.propertyId || "NA",
      term: data.term || "NA",
      "dates selected": data.datesSelected || "NA",
      "num of guests": data.numOfGuests || "NA",
      "page origin": data.pageOrigin || "NA",
      "total display price excluding taxes":
        data.totalDisplayPriceExcludingTaxes || "NA",
    })
  }

  CLICK_PRICE_DROP_WATCH_CREATED(data: {
    type: "Watch Property" | "Watch Trip"
    propertyId?: string
    term?: string | null
    datesSelected: string
    numOfGuests: number
    pageOrigin: "search results page" | "property detail page"
    totalDisplayPriceExcludingTaxes: number | null
  }) {
    this.track(EventENUMV2.CLICK_PRICE_DROP_WATCH_CREATED, {
      type: data.type,
      "property id": data.propertyId || "NA",
      term: data.term || "NA",
      "dates selected": data.datesSelected || "NA",
      "num of guests": data.numOfGuests || "NA",
      "page origin": data.pageOrigin || "NA",
      "total display price excluding taxes":
        data.totalDisplayPriceExcludingTaxes || "NA",
    })
  }

  CLICK_PRICE_DROP_WATCH_REMOVED(data: {
    type: "Watch Property" | "Watch Trip"
    propertyId?: string
    term?: string | null
    datesSelected: string
    numOfGuests: number
    pageOrigin: "search results page" | "property detail page"
    totalDisplayPriceExcludingTaxes: number | null
    currentPrice: number | null
  }) {
    this.track(EventENUMV2.CLICK_PRICE_DROP_WATCH_REMOVED, {
      type: data.type,
      "property id": data.propertyId || "NA",
      term: data.term || "NA",
      "dates selected": data.datesSelected || "NA",
      "num of guests": data.numOfGuests || "NA",
      "page origin": data.pageOrigin || "NA",
      "total display price excluding taxes":
        data.totalDisplayPriceExcludingTaxes || "NA",
      "current price": data.currentPrice || "NA",
    })
  }

  CLICK_VIEW_WATCHLIST_PROPERTY(data: {
    type: "Watch Property" | "Watch Trip"
    propertyId?: string
    term?: string | null
    datesSelected: string
    numOfGuests: number
    pageOrigin:
      | "search results page"
      | "price dropped alert page"
      | "watchlist page"
    totalDisplayPriceExcludingTaxes: number | null
    droppedPrice: number | null
    price_drop: "yes" | "no"
  }) {
    this.track(EventENUMV2.CLICK_VIEW_WATCHLIST_PROPERTY, {
      type: data.type,
      "property id": data.propertyId || "NA",
      term: data.term || "NA",
      "dates selected": data.datesSelected || "NA",
      "num of guests": data.numOfGuests || "NA",
      "page origin": data.pageOrigin || "NA",
      "total display price excluding taxes":
        data.totalDisplayPriceExcludingTaxes || "NA",
      "dropped price": data.droppedPrice || "NA",
      price_drop: data.price_drop,
    })
  }

  CLICK_PRICE_DROP_WATCH_ERROR(data: {
    type: "Watch Property" | "Watch Trip"
    propertyId?: string
    term?: string | null
    datesSelected: string
    numOfGuests: number
    pageOrigin: "search results page" | "property detail page"
    totalDisplayPriceExcludingTaxes: number | null
    errorMessage: string
  }) {
    this.track(EventENUMV2.CLICK_PRICE_DROP_WATCH_ERROR, {
      type: data.type,
      "property id": data.propertyId || "NA",
      term: data.term || "NA",
      "dates selected": data.datesSelected || "NA",
      "num of guests": data.numOfGuests || "NA",
      "page origin": data.pageOrigin || "NA",
      "total display price excluding taxes":
        data.totalDisplayPriceExcludingTaxes || "NA",
      "error message": data.errorMessage || "NA",
    })
  }

  // price drop events end

  // soft login events
  VIEW_SOFT_LOGIN_PROMPT(data: {
    pageOrigin:
      | "price drop alert"
      | "property favorite"
      | "ask question to host"
  }) {
    this.track(EventENUMV2.VIEW_SOFT_LOGIN_PROMPT, {
      "page origin": data?.pageOrigin?.toLowerCase() || "NA",
    })
  }

  COMPLETE_SOFT_LOGIN(data: {
    pageOrigin:
      | "price drop alert"
      | "property favorite"
      | "ask question to host"
    emailId: string
    signupType: "email" | "google" | "apple"
  }) {
    this.track(EventENUMV2.COMPLETE_SOFT_LOGIN, {
      "page origin": data?.pageOrigin?.toLowerCase() || "NA",
      "email id": data?.emailId || "NA",
      "signup type": data?.signupType || "NA",
    })
  }

  EXIT_SOFT_LOGIN(data: {
    phoneOptIn: boolean
    createdFullAccount: boolean
    source?: string
  }) {
    this.track(EventENUMV2.EXIT_SOFT_LOGIN, {
      "phone opt in": data?.phoneOptIn || false,
      "created full account": data?.createdFullAccount || false,
      "page origin": data?.source || "NA",
    })
  }

  // Multi Units event
  VIEW_MULTI_UNIT_DETAIL_PAGE(data: {
    pageOrigin: string
    propertyId: string
    city: string
    state: string
    datesSelected: string
    numOfNights: number
    propertyType: string
    numOfDaysUntilCheckIn: number | null
    searchPagePrice?: string
    searchPageSaveAmount?: string
    searchPageStrikeOutPrice?: string
  }) {
    this.track(EventENUMV2.VIEW_MULTI_UNIT_DETAIL_PAGE, {
      "page origin": data?.pageOrigin?.toLowerCase() || "NA",
      "city of property": data?.city || "NA",
      "dates selected": data?.datesSelected || "NA",
      "mp schema version": "1.0",
      "num of days until check in": data?.numOfDaysUntilCheckIn || "NA",
      "num of nights": data?.numOfNights || "NA",
      "property id": data?.propertyId || "NA",
      "search page price": data?.searchPagePrice || "NA",
      "search page save amount": data?.searchPageSaveAmount || "NA",
      "search page strike out price": data?.searchPageStrikeOutPrice || "NA",
      "state of property": data?.state || "NA",
      "type of property": data?.propertyType || "NA",
    })
  }

  CLICK_MULTI_UNIT_DETAIL_CARD(data: {
    pageOrigin: string
    propertyId: string
  }) {
    this.track(EventENUMV2.CLICK_MULTI_UNIT_DETAIL_CARD, {
      "page origin": data?.pageOrigin?.toLowerCase() || "NA",
      "property id": data?.propertyId || "NA",
    })
  }

  CLICK_OPEN_SEARCH(data: {
    pageOrigin: string
    actionType: "initial" | "modify"
  }) {
    this.track(EventENUMV2.CLICK_OPEN_SEARCH, {
      "page origin": data?.pageOrigin?.toLowerCase() || "NA",
      "action type": data?.actionType || "NA",
    })
  }

  CLICK_OPEN_FILTERS(data: { pageOrigin: string }) {
    this.track(EventENUMV2.CLICK_OPEN_FILTERS, {
      "page origin": data?.pageOrigin?.toLowerCase(),
    })
  }

  // on watchlist page event start.
  VIEW_WATCHLIST_PAGE(data: { theme: string }) {
    this.track(EventENUMV2.VIEW_WATCHLIST_PAGE, {
      "mp schema version": "1.0",
      theme: data.theme,
    })
  }

  CLICK_REMOVE_FAVORITE() {
    this.track(EventENUMV2.CLICK_REMOVE_FAVORITE, {})
  }

  CLICK_REMOVE_PRICE_ALERT() {
    this.track(EventENUMV2.CLICK_REMOVE_PRICE_ALERT, {})
  }

  CLICK_CONFIRM_REMOVAL() {
    this.track(EventENUMV2.CLICK_CONFIRM_REMOVAL, {})
  }

  CLICK_LEARN_MORE_LINK() {
    this.track(EventENUMV2.CLICK_LEARN_MORE_LINK, {})
  }

  CLICK_PRICE_INFO_ICON() {
    this.track(EventENUMV2.CLICK_PRICE_INFO_ICON, {})
  }
  // ---------- watchlist page event end. -----

  TA_LINK_GENERATION_CLICK(data: {
    agentId: string | number
    propertyId: string
  }) {
    this.track(EventENUMV2.TA_LINK_GENERATION_CLICK, {
      "agent id": data?.agentId || "NA",
      "property id": data?.propertyId || "NA",
    })
  }

  TA_LINK_GENERATED(data: {
    agentId: string | number
    link: string
    propertyId: string
  }) {
    this.track(EventENUMV2.TA_LINK_GENERATED, {
      "agent id": data?.agentId || "NA",
      link: data?.link || "NA",
      "property id": data?.propertyId || "NA",
    })
  }

  TA_STRIPE_ACCOUNT_CREATION_CLICK(data: { agentId: string | number }) {
    this.track(EventENUMV2.TA_STRIPE_ACCOUNT_CREATION_CLICK, {
      "agent id": data?.agentId || "NA",
    })
  }
}

export const mixpanelV2Client = new MixpanelV2()
